import { computed, reactive, ref, type Ref, type UnwrapRef } from "vue";

/**
 * Util for states where only 1 value on a list can be active at a time.
 *
 * @example
 * /// Definition
 * enum ProducerControlsMenu {
 *   AGENDA = "Agenda",
 *   FANS = "Fans",
 *   CONTEST = "Contest",
 *   ADMIN = "Admin",
 * }
 *
 * const activeMenu = useEnumRef(
 *   [
 *     ProducerControlsMenu.AGENDA,
 *     ProducerControlsMenu.FANS,
 *     ProducerControlsMenu.CONTEST,
 *     ProducerControlsMenu.ADMIN,
 *   ],
 *   null,
 * );
 *
 * /// Usage (note the lowercased members)
 * if (activeMenu.agenda) {
 *   // Do something
 * }
 *
 * console.log(activeMenu.value); // returns the active menu value (not whether it's active)
 *
 * // Set the active menu
 * activeMenu.fans = true;
 * activeMenu.value = ProducerControlsMenu.FANS; // verbose alternative
 */
export function useActiveEnumValue<T extends string, D>(
  options: T[],
  defaultValue: D,
) {
  const active = ref<T | D>(defaultValue);

  const optionValues = options.reduce(
    (options, option) => {
      const key = option.toLocaleLowerCase() as Lowercase<T>;
      const value = computed({
        get: () => active.value === option,
        set: (newValue: boolean) => {
          if (newValue) {
            active.value = option as UnwrapRef<T>;
          } else {
            active.value = defaultValue as UnwrapRef<D>;
          }
        },
      });

      options[key] = value;
      return options;
    },
    {} as Record<Lowercase<T>, Ref<boolean>>,
  );

  return reactive({
    ...optionValues,
    value: active,
  });
}
